import React, { useCallback, useEffect, useRef, useState } from "react";
import { Accordion, Dropdown } from "react-bootstrap";
import type { DropDirection } from "react-bootstrap/esm/DropdownContext";
import { default as MapIcon } from '@assets/icons/map-marker.svg?react'
import { getRegions } from "@api/getregions";
import type { Region, StateRegion } from "@api/getregions";

import css from './Select.module.scss'
import scss from "./Location.module.scss";

type RegionHandlerType = React.Dispatch<React.SetStateAction<Region | null>> | ((region: Region) => void);

interface LocationSearchProps {
  initialRegionId?: number
  direction?: DropDirection
  label?: string
  placeholder?: string,
  currentRegion: Region | null
  customButtonClass?: string
  customDropdownClass?: string
  showIcon?: boolean
  setCurrentRegion: RegionHandlerType
}

function LocationSearch({ initialRegionId, direction, label, placeholder = 'Select Location', currentRegion, customButtonClass, customDropdownClass, showIcon = true, setCurrentRegion }: LocationSearchProps) {
  const [stateRegions, setStateRegions] = useState<StateRegion[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    function initialiseRegion(states: StateRegion[]) {
      if (!!initialRegionId) {
        const region = states.flatMap(state => state.Regions).find(region => region.Id === initialRegionId);
        if (!!region) {
          setCurrentRegion(region);
        }
      }
    }

    if (!stateRegions) {
      setIsLoading(true);
      getRegions().then(
        result => {
          setStateRegions(result);
          initialiseRegion(result);
        }
      ).finally(() => {
        setIsLoading(false);
      });
    }
    else {
      initialiseRegion(stateRegions)
    }
  }, [stateRegions, initialRegionId])

  const refButton = useRef<HTMLButtonElement>(null);

  const setRegion = useCallback((e: React.MouseEvent, region: Region) => {
    e.preventDefault();
    setCurrentRegion(region);
    refButton.current?.click();
  }, [refButton]);

  const getCurrentRegionName = () => {
    if (currentRegion?.Name) {
        return currentRegion.Name;
    }

    const region = currentRegion?.Id 
        ? stateRegions?.flatMap(state => state.Regions).find(region => region.Id === currentRegion.Id) 
        : null;

    return region?.Name || placeholder;
  };

  return <Dropdown className={`region-dropdown ${css.select} ${label ? css.withLabel : ''} ${!!customDropdownClass ? customDropdownClass : ''}`} drop={direction}>
    <Dropdown.Toggle
      ref={refButton}
      variant="dropdown"
      id="ew-search-location"
      data-testid="locationsearch-btn-toggle"
      disabled={isLoading}
      className={!!customButtonClass ? customButtonClass : undefined}
    >
      {!!showIcon && <MapIcon className={scss.mapIcon} width={20} /> }
      {label ? <div className={css.trigger}>
          <span className={css.label}>{label}</span>
          <span className={css.value} data-testid="locationsearch-region">{currentRegion ? getCurrentRegionName() : placeholder}</span>
        </div>
        : <span data-testid="locationsearch-region">{currentRegion ? getCurrentRegionName() : placeholder}</span>}
      
    </Dropdown.Toggle>
    <Dropdown.Menu className="w-100 p-0">
      <Accordion data-testid={stateRegions ? 'location-search-regions' : ''}>
        {
          stateRegions?.map((state, i) => {
            return (<Accordion.Item eventKey={(i + 1).toString()} key={state.StateId}>
              <Accordion.Button bsPrefix="dropdown-item dropdown-toggle fw-bold pt-2 pb-2">
                {state.StateName}
              </Accordion.Button>
              <Accordion.Body>
                {state.Regions.map((region, regCount) => {
                  return (<a key={region.Id} href='#' className="dropdown-item" onClick={(e) => setRegion(e, region)}>{region.Name}</a>)
                })}
              </Accordion.Body>
            </Accordion.Item>)
          })
        }
      </Accordion>
    </Dropdown.Menu>
  </Dropdown>
}

export default LocationSearch
